/**
 * "How to find" — guide photos for a station. Three fixed types: entrance, street view, landmark.
 * No free comments; type is sent as comment_0, comment_1, comment_2 so backend/client can use it.
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  GUIDE_PHOTO_TYPES,
  mapUrlsToGuidePhotoSlots,
  normalizeGuidePhotosValue,
} from '../constants/guidePhotoTypes';

const MIN_PHOTOS = 3;

function generateId() {
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function GuidePhotosField({ value = [], onChange, error, minPhotos = MIN_PHOTOS }) {
  const { t } = useTranslation();
  const slots = React.useMemo(() => normalizeGuidePhotosValue(value), [value]);
  const fileInputRefs = React.useRef({});

  const updateSlot = React.useCallback(
    (type, patch) => {
      const next = slots.map((s) => (s.type === type ? { ...s, ...patch } : s));
      onChange(next);
    },
    [slots, onChange],
  );

  const handleFileChange = React.useCallback(
    (type, e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const previewUrl = URL.createObjectURL(file);
      updateSlot(type, {
        id: generateId(),
        file,
        previewUrl,
        existingUrl: null,
      });
      e.target.value = '';
    },
    [updateSlot],
  );

  const handleRemove = React.useCallback(
    (type) => {
      const slot = slots.find((s) => s.type === type);
      if (slot?.previewUrl) URL.revokeObjectURL(slot.previewUrl);
      updateSlot(type, { file: null, previewUrl: null, existingUrl: null });
    },
    [slots, updateSlot],
  );

  const triggerFileInput = (type) => {
    const ref = fileInputRefs.current[type];
    if (ref) ref.click();
  };

  React.useEffect(() => {
    return () => {
      slots.forEach((s) => {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      });
    };
  }, []);

  const withPhotoCount = slots.filter((s) => s.file || s.existingUrl).length;
  const hasError = !!error || (minPhotos > 0 && withPhotoCount > 0 && withPhotoCount < minPhotos);

  return (
    <Paper variant="outlined" sx={{ mt: 3, p: 2, width: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {t('stationPage.guidePhotosTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('stationPage.guidePhotosDescription')}
      </Typography>
      <Stack spacing={2}>
        {GUIDE_PHOTO_TYPES.map((type) => {
          const slot = slots.find((s) => s.type === type) || { type, id: type, file: null, previewUrl: null, existingUrl: null };
          const imageUrl = slot.previewUrl || slot.existingUrl;
          const hasImage = !!imageUrl;
          return (
            <Paper
              key={slot.type}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: hasImage ? 'divider' : 'action.disabledBackground',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t(`stationPage.guidePhotoType_${type}`)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t(`stationPage.guidePhotoHint_${type}`)}
              </Typography>
              <input
                ref={(el) => { fileInputRefs.current[type] = el; }}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(type, e)}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {hasImage ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={{
                      width: 140,
                      height: 105,
                      objectFit: 'cover',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 140,
                      height: 105,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t('stationPage.addPhoto')}
                    </Typography>
                  </Box>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddPhotoAlternateOutlinedIcon />}
                    onClick={() => triggerFileInput(type)}
                  >
                    {hasImage ? t('stationPage.replacePhoto') : t('stationPage.addPhoto')}
                  </Button>
                  {hasImage && (
                    <IconButton
                      size="small"
                      aria-label={t('stationPage.removePhoto')}
                      onClick={() => handleRemove(type)}
                      color="error"
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Stack>
      {hasError && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error || (minPhotos > 0 && withPhotoCount > 0 && withPhotoCount < minPhotos ? t('stationPage.uploadAtLeastPhotos', { count: minPhotos }) : null)}
        </FormHelperText>
      )}
    </Paper>
  );
}

GuidePhotosField.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['streetView', 'entrance', 'landmark']).isRequired,
      id: PropTypes.string,
      file: PropTypes.instanceOf(File),
      previewUrl: PropTypes.string,
      existingUrl: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  minPhotos: PropTypes.number,
};
